import { useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";

export default function Projects() {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-r from-seafarer-orange/10 to-seafarer-purple/10">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-6">Our Projects</h1>
              <p className="text-lg text-white">
                Discover our ongoing and completed housing development projects aimed at improving 
                the lives of fishermen and their families.
              </p>
            </div>
          </div>
        </section>
        
        {/* Main Project */}
        <section className="section-padding">
          <div className="page-container">
            <div className="grid grid-cols-1 gap-12">
              <div>
                <h2 className="text-white text-3xl font-bold mb-6">Fisherman Village at Farm 37</h2>
                <p className="text-white mb-4">
                  Our first priority is to purchase land and develop at least 150 houses in the first 
                  phase of the Project, on a land mass that covers at least 48.2575 hectares, at 
                  Portion 18 of Farm 37.
                </p>
                <p className="text-white mb-4">
                  After receiving conditional approval and reservation by the Municipality of Walvis Bay 
                  Ordinary Council Meeting, we are moving forward with plans to develop this fisherman 
                  village that will provide quality housing for our members.
                </p>
                <p className="text-white mb-6">
                  This project aims to improve the quality of environment while forming architectural 
                  beauty factors that will increase income and improve the welfare of fishermen and 
                  their families.
                </p>
                
                <h3 className="text-white text-xl font-semibold mb-4">Project Highlights:</h3>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-white">
                  <li>150 quality houses in Phase 1</li>
                  <li>Green open spaces and sustainable design</li>
                  <li>Direct support to fisherman families</li>
                  <li>Community amenities and infrastructure</li>
                </ul>
                <a
                  href="/documents/Preliminary Plan_28-02-2025_250301_193054.pdf"
                  download
                  className="inline-flex items-center px-6 py-3 bg-seafarer-orange text-white font-semibold rounded-lg shadow hover:bg-orange-600 transition-colors mb-8"
                >
                  Download Preliminary Plan PDF
                </a>
                
                <Button className="seafarer-btn-primary">
                  View Project Details <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>
        
        {/* Project Timeline */}
        <section className="section-padding relative overflow-hidden bg-gradient-to-b from-white via-gray-50 to-white dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-seafarer-orange/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-seafarer-purple/5 rounded-full blur-3xl"></div>
          
          <div className="page-container relative z-10">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 gradient-text">Project Timeline</h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Our roadmap for building 1200 houses within the period of 6-10 years.
              </p>
            </div>
            
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-seafarer-orange/50 via-seafarer-purple/50 to-seafarer-blue/50 transform md:translate-x-[-50%]"></div>
              
              {/* Timeline items */}
              <div className="space-y-12">
                {[
                  {
                    year: "2021",
                    title: "Federation Establishment",
                    description: "Formation of the October Seafarers United Land & Housing Federation in Walvis Bay by Mr. Matthias M. Ndeulita.",
                    achievements: [
                      "Recruited 420+ members",
                      "Raised N$ 880,000.00 through monthly contributions",
                      "Secured conditional approval for Farm 37"
                    ]
                  },
                  {
                    year: "2022-2023",
                    title: "Phase 1 Development",
                    description: "Development of the first 150 houses at Portion 18 of Farm 37.",
                    achievements: [
                      "Land acquisition and preparation",
                      "Infrastructure development",
                      "Construction of initial housing units",
                      "Community facilities planning"
                    ]
                  },
                  {
                    year: "2024-2026",
                    title: "Expansion Phase",
                    description: "Expansion to additional coastal towns including Swakopmund, Henties Bay, and Luderitz.",
                    achievements: [
                      "Additional land acquisition",
                      "300+ more housing units",
                      "Expansion of federation membership",
                      "Development of community services"
                    ]
                  },
                  {
                    year: "2027-2031",
                    title: "Completion Phase",
                    description: "Completion of the 1200 houses goal and establishment of sustainable community infrastructure.",
                    achievements: [
                      "Remaining housing units construction",
                      "Full community amenities development",
                      "Establishment of long-term management structure",
                      "Sustainability initiatives implementation"
                    ]
                  }
                ].map((item, index) => (
                  <div 
                    key={item.year}
                    className={`relative flex flex-col md:flex-row items-center ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    } animate-fade-in`}
                    style={{ animationDelay: `${index * 200}ms` }}
                  >
                    <div className="flex-1 md:w-1/2 mb-6 md:mb-0">
                      <div 
                        className={`seafarer-card p-6 ${
                          index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'
                        } hover:shadow-lg transition-all duration-300`}
                      >
                        <span className="inline-block px-3 py-1 rounded-full bg-seafarer-orange/10 text-seafarer-orange text-sm font-semibold mb-3">
                          {item.year}
                        </span>
                        <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-4">{item.description}</p>
                        <ul className="space-y-1">
                          {item.achievements.map((achievement, i) => (
                            <li key={i} className="flex items-start">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-seafarer-orange mt-1.5 mr-2"></span>
                              <span className="text-gray-600 dark:text-gray-300 text-sm">{achievement}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    <div className="md:absolute left-1/2 transform -translate-x-1/2 z-10 w-8 h-8 flex items-center justify-center">
                      <div className="w-5 h-5 rounded-full bg-seafarer-orange shadow-lg"></div>
                    </div>
                    
                    <div className="flex-1 md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        <CTASection />
      </main>
      
      <Footer />
    </div>
  );
}
